import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import {
  deleteCompanies,
  getAllCompanies,
  getAllCompanyContacts,
  getCompanies as fetchSingleCompany,
  updateCompanies,
} from '@/service/companies'
import type { Company, User } from '@/views/companies/data/schema'

export const useCompanyStore = defineStore('company', () => {
  const companies = ref<Company[]>([])
  const companyContacts = ref<User[]>([])
  const paginationCompanies = ref<{
    pageSize: number
    pageIndex: number
    meta: {
      has_more?: boolean
      has_prev?: boolean
      page?: number
      limit: number
      total_pages: number
      total_items: number
      next_page: number | null
      prev_page: number | null
    } | null
  }>({
    pageSize: 10,
    pageIndex: 0,
    meta: null,
  })
  const paginationClients = ref<{
    pageSize: number
    pageIndex: number
    meta: {
      has_more?: boolean
      has_prev?: boolean
      page?: number
      limit: number
      total_pages: number
      total_items: number
      next_page: number | null
      prev_page: number | null
    } | null
  }>({
    pageSize: 10,
    pageIndex: 0,
    meta: null,
  })

  const isLoadingCompanies = ref(false)
  const isLoadingContacts = ref(false)

  const fetchCompanies = async (enriched = true) => {
    isLoadingCompanies.value = true
    const { onSuccess, onError } = getAllCompanies(enriched)

    return new Promise<void>((resolve, reject) => {
      onSuccess((data) => {
        companies.value = data.map((company: any) => {
          const mapped = {
            ...company,
            accessorKey: 'company',
            memberCount: company.meta?.totalMembers ?? 0,
            ticketCount: company.meta?.totalTickets ?? 0,
          }

          return mapped
        })
        isLoadingCompanies.value = false
        resolve(data)
      })
      onError((error) => {
        console.error('[fetchCompanies] Error fetching companies:', error)
        isLoadingCompanies.value = false
        reject(error)
      })
    })
  }

  const getCompanyById = (id: string) => {
    return companies.value.find((company) => company.id === id)
  }
  const fetchCompanyContacts = async (id: string) => {
    const { onSuccess, onError } = getAllCompanyContacts(id)

    return new Promise<void>((resolve) => {
      onSuccess((data) => {
        companyContacts.value = data
        resolve(data)
      })
      onError(() => {
        resolve()
      })
    })
  }

  const removeCompany = async (id: string) => {
    isLoadingCompanies.value = true
    const { onSuccess, onError } = deleteCompanies(id)
    const remainingCompanies = companies.value.filter((company) => company.id !== id)

    return new Promise<void>((resolve, reject) => {
      onSuccess(() => {
        companies.value = remainingCompanies
        isLoadingCompanies.value = false
        resolve()
      })
      onError((error) => {
        isLoadingCompanies.value = false
        reject(error)
      })
    })
  }
  const updateCompany = async (id: string, data: Company) => {
    const { onSuccess, onError } = updateCompanies(id, data)

    return new Promise<void>((resolve, reject) => {
      onSuccess((data) => {
        const modifiedCompany = companies.value.find((company) => company.id === id)
        const companyObj = { ...modifiedCompany, ...data }

        const idx = companies.value.findIndex((c) => c.id === id)

        if (idx === -1) {
          resolve()

          return
        }

        companies.value.splice(idx, 1, companyObj)

        resolve()
      })
      onError((error) => {
        reject(error)
      })
    })
  }

  const addCompanyContacts = (contacts: User[]) => {
    companyContacts.value.push(...contacts)
  }
  const setCompanies = async (newCompanies: Company[]) => {
    companies.value = newCompanies.map((company: any) => {
      return {
        ...company,
        accessorKey: 'company',
        memberCount: company.meta?.totalMembers ?? company.memberCount ?? 0,
        ticketCount: company.meta?.totalTickets ?? company.ticketCount ?? 0,
      }
    })
  }
  const setPaginationCompanies = async (newPagination) => {
    paginationCompanies.value = newPagination
  }
  const setPaginationClients = async (newPagination) => {
    paginationClients.value = newPagination
  }
  const updateCompanyMemberCount = (companyId: string | null | undefined, count: number) => {
    if (!companyId) return

    const company = companies.value.find((c) => c.id === companyId)

    if (!company) return

    company.meta.totalMembers = (company.meta.totalMembers || 0) + count
  }
  const removeCompanyMainContact = async (companyId: string | null | undefined) => {
    if (!companyId) return
    const currentCompany = companies.value.find((c) => c.id === companyId)

    if (!currentCompany) return
    await updateCompanies(companyId as string, {
      mainContactId: null,
    })
    currentCompany.mainContactId = null
    currentCompany.mainContact = null
  }
  const updateCompanyPhone = (companyId: string | null | undefined, phone: string) => {
    if (!companyId) return

    const company = companies.value.find((c) => c.id === companyId)

    if (!company) return

    company.phone = phone
  }

  const getCompanies = computed(() => companies.value)

  const getUserById = (id: string) => {
    const allUsers = companies.value.map((org) => org.featuredUsers).flat()

    return allUsers.find((user) => user.id === id)
  }

  const changeCompanyDetails = (company: any) => {
    const index = companies.value.findIndex((c) => c.id === company.id)

    if (index !== -1) {
      const updatedCompany = {
        ...company,
        memberCount: company.meta?.totalMembers ?? company.memberCount ?? companies.value[index].memberCount ?? 0,
        ticketCount: company.meta?.totalTickets ?? company.ticketCount ?? companies.value[index].ticketCount ?? 0,
      }

      // Use splice to trigger Vue reactivity properly
      companies.value.splice(index, 1, updatedCompany)
    }
  }

  const clearCompanies = () => {
    companies.value = []
  }

  const refreshSingleCompany = async (companyId: string) => {
    const { onSuccess, onError } = fetchSingleCompany(companyId, true)

    return new Promise<void>((resolve, reject) => {
      onSuccess((updatedCompany: any) => {
        const index = companies.value.findIndex((c) => c.id === companyId)

        if (index !== -1) {
          const mapped = {
            ...updatedCompany,
            accessorKey: 'company',
            memberCount: updatedCompany.meta?.totalMembers ?? 0,
            ticketCount: updatedCompany.meta?.totalTickets ?? 0,
          }

          companies.value.splice(index, 1, mapped)
        } else {
          console.warn('[CompanyStore] Company not found in store:', companyId)
        }
        resolve()
      })
      onError((error: any) => {
        console.error('[refreshSingleCompany] Error refreshing company:', error)
        reject(error)
      })
    })
  }

  return {
    companies,
    fetchCompanies,
    setCompanies,
    getUserById,
    clearCompanies,
    refreshSingleCompany,
    getCompanies,
    isLoadingCompanies,
    fetchCompanyContacts,
    companyContacts,
    isLoadingContacts,
    getCompanyById,
    removeCompany,
    updateCompany,
    paginationCompanies,
    setPaginationCompanies,
    paginationClients,
    setPaginationClients,
    updateCompanyMemberCount,
    updateCompanyPhone,
    changeCompanyDetails,
    addCompanyContacts,
    removeCompanyMainContact,
  }
})
